import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Toast from './Toast';
import { authAPI } from '../services/api';
import authVisual from '../assets/auth/buyer-auth-visual.png';

const logo = '/autoboy_logo3.png';

/**
 * Landing page for the link in the password-reset email
 * (/reset-password?token=...). The backend has always been able to reset a
 * password with that token, but there was no page here to hand it to — the
 * email led to a 404.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!token) {
      setErrors({ general: 'This reset link is missing its token. Request a new one from the login page.' });
    }
  }, [token]);

  // Same rule the backend enforces (utils.IsValidPassword), so a password that
  // looks fine here can't be rejected by the API.
  const isStrong = (p) =>
    p.length >= 8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!password) next.password = 'Enter a new password';
    else if (!isStrong(password))
      next.password = 'Use 8+ characters with uppercase, lowercase, a number, and a special character';
    if (password !== confirmPassword) next.confirmPassword = 'Passwords do not match';

    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
      setToast({ message: 'Password updated. You can sign in now.', type: 'success' });
      setTimeout(() => navigate('/auth'), 2500);
    } catch (error) {
      setErrors({
        general:
          error.message ||
          'This reset link is invalid or has expired. Request a new one from the login page.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-11 tw-pr-11 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10';

  return (
    <div className="tw-min-h-dvh tw-w-full tw-bg-[#0A0A0A] tw-flex tw-flex-col lg:tw-flex-row tw-font-['Epilogue',_sans-serif]">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Left — Brand Visual */}
      <div className="tw-relative tw-hidden lg:tw-flex lg:tw-w-[46%] xl:tw-w-1/2 tw-flex-col tw-justify-between tw-overflow-hidden tw-bg-black">
        <img
          src={authVisual}
          alt=""
          aria-hidden="true"
          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover"
        />
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black tw-via-black/60 tw-to-black/20" />
        <div className="tw-relative tw-z-10 tw-p-10 xl:tw-p-14">
          <a
            href="/auth"
            className="tw-inline-flex tw-items-center tw-gap-2 tw-text-white/90 hover:tw-text-white tw-transition-colors tw-text-sm tw-font-medium"
          >
            <i className="fas fa-arrow-left tw-text-xs" />
            Back to sign in
          </a>
        </div>
        <div className="tw-relative tw-z-10 tw-p-10 xl:tw-p-14 tw-pb-14 xl:tw-pb-16">
          <img src={logo} alt="AutoboyExpress" className="tw-h-36 xl:tw-h-44 tw-w-auto tw-mb-8 tw-drop-shadow-[0_2px_28px_rgba(34,197,94,0.45)]" />
          <h1 className="tw-text-[2.5rem] xl:tw-text-[3rem] tw-font-extrabold tw-leading-[1.05] tw-tracking-tight tw-text-white tw-mb-4">
            Set a new<br />
            <span className="tw-bg-gradient-to-r tw-from-green-400 tw-to-green-500 tw-bg-clip-text tw-text-transparent">password.</span>
          </h1>
          <p className="tw-text-white/60 tw-text-base tw-max-w-md tw-leading-relaxed">
            Choose something you haven't used here before. The link in your email expires an hour after it was sent.
          </p>
        </div>
      </div>

      {/* Right — Form */}
      <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-py-10 sm:tw-px-10 lg:tw-px-16 tw-min-h-dvh tw-overflow-y-auto">
        <div className="tw-w-full tw-max-w-[420px] tw-py-6">
          <img src={logo} alt="AutoboyExpress" className="tw-h-20 tw-w-auto tw-mb-8 tw-mx-auto" />

          {done ? (
            <div className="tw-text-center">
              <div className="tw-inline-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-green-500/10 tw-border tw-border-green-500/25 tw-mb-5">
                <i className="fas fa-check tw-text-green-400 tw-text-xl" />
              </div>
              <h2 className="tw-text-2xl tw-font-bold tw-text-white tw-mb-2">Password updated</h2>
              <p className="tw-text-white/45 tw-text-sm tw-mb-6">Taking you to the sign-in page...</p>
              <a href="/auth" className="tw-font-semibold tw-text-green-400 hover:tw-text-green-300">Sign in now</a>
            </div>
          ) : (
            <>
              <h2 className="tw-text-3xl tw-font-bold tw-text-white tw-mb-1.5 tw-tracking-tight">Reset your password</h2>
              <p className="tw-text-white/45 tw-text-sm tw-mb-8">Enter a new password for your account.</p>

              <form className="tw-flex tw-flex-col tw-gap-5" onSubmit={handleSubmit}>
                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-white/80 tw-mb-2">New Password</label>
                  <div className="tw-relative">
                    <i className="fas fa-lock tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={inputCls}
                      placeholder="Create a new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/35 hover:tw-text-green-400 tw-transition-colors"
                    >
                      <i className={`far ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                    </button>
                  </div>
                  {errors.password && <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{errors.password}</p>}
                </div>

                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-white/80 tw-mb-2">Confirm Password</label>
                  <div className="tw-relative">
                    <i className="fas fa-lock tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={inputCls}
                      placeholder="Re-enter your new password"
                      autoComplete="new-password"
                    />
                  </div>
                  {errors.confirmPassword && <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{errors.confirmPassword}</p>}
                </div>

                {errors.general && (
                  <div className="tw-rounded-lg tw-bg-red-500/10 tw-border tw-border-red-500/20 tw-px-4 tw-py-2.5 tw-text-center tw-text-sm tw-font-medium tw-text-red-400">
                    {errors.general}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || !token}
                  className="tw-mt-1 tw-w-full tw-rounded-xl tw-bg-gradient-to-r tw-from-green-500 tw-to-green-600 tw-py-3.5 tw-text-base tw-font-bold tw-text-white tw-shadow-lg tw-shadow-green-500/20 tw-transition-all hover:-tw-translate-y-0.5 hover:tw-shadow-xl hover:tw-shadow-green-500/30 active:tw-translate-y-0 active:tw-scale-[0.98] disabled:tw-opacity-60 disabled:tw-pointer-events-none"
                >
                  {submitting ? 'Updating...' : 'Update Password'}
                </button>
              </form>

              <p className="tw-text-center tw-text-sm tw-text-white/45 tw-mt-8">
                Remembered it?{' '}
                <a href="/auth" className="tw-font-semibold tw-text-green-400 hover:tw-text-green-300 tw-transition-colors">Sign in</a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
